import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen, Loader2 } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { formatError } from "@/api/client";
import { toast } from "sonner";

export default function RegisterPage() {
  const navigate = useNavigate();
  const register = useAuthStore((s) => s.register);
  const [form, setForm] = useState({ full_name: "", email: "", password: "", org_name: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created!");
      navigate("/dashboard");
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="w-10 h-10 bg-[#41B5E6] flex items-center justify-center rounded-sm">
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <div className="font-black text-lg tracking-tight">Handle Docs</div>
        </Link>

        <div className="bg-white border border-zinc-200 p-8 rounded-sm">
          <h1 className="text-2xl font-bold mb-1">Create your workspace</h1>
          <p className="text-sm text-zinc-500 mb-6">Spin up a new docs hub in seconds.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div>
              <label className="block text-sm font-semibold mb-1.5">Full name</label>
              <input required value={form.full_name} onChange={set("full_name")} data-testid="register-name"
                className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#41B5E6]" />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1.5">Organization name</label>
              <input value={form.org_name} onChange={set("org_name")} data-testid="register-org" placeholder="Acme Inc."
                className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#41B5E6]" />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1.5">Email</label>
              <input type="email" required value={form.email} onChange={set("email")} data-testid="register-email"
                className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#41B5E6]" />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1.5">Password</label>
              <input type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="register-password"
                className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#41B5E6]" />
            </div>
            {error && (
              <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 px-3 py-2 rounded-sm">{error}</div>
            )}
            <button type="submit" disabled={loading} data-testid="register-submit"
              className="w-full bg-[#41B5E6] hover:bg-[#2A9BCC] text-white font-semibold py-2.5 rounded-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Create account
            </button>
          </form>

          <p className="text-sm text-center mt-6 text-zinc-600">
            Already have an account?{" "}
            <Link to="/login" className="text-[#41B5E6] font-semibold hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
